import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, GraduationCap, MailCheck, ArrowLeft } from "lucide-react";

export function AuthModal({ open, onOpenChange, defaultTab = "login" }) {
  const { login, register, forgotPassword, formatApiErrorDetail } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  useEffect(() => { if (open) { setTab(defaultTab); setShowForgot(false); setForgotSent(false); } }, [open, defaultTab]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitForgot = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const email = (fd.get("email") ?? form.email ?? "").toString().trim();
    setLoading(true);
    try {
      await forgotPassword(email);
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    // Read straight from the form so browser/Apple autofill values are captured
    // even when React's onChange didn't fire (a common cause of false "invalid" errors).
    const fd = new FormData(e.currentTarget);
    const email = (fd.get("email") ?? form.email ?? "").toString().trim();
    const password = (fd.get("password") ?? form.password ?? "").toString();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = (fd.get("name") ?? form.name ?? "").toString().trim();
    const email = (fd.get("email") ?? form.email ?? "").toString().trim();
    const password = (fd.get("password") ?? form.password ?? "").toString();
    setLoading(true);
    try {
      const u = await register(name, email, password);
      toast.success(`Enrolment confirmed. Welcome, ${u.name}.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-10 w-10 rounded-lg bg-navy flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-persian-blue" />
            </div>
            <DialogTitle className="font-serif text-2xl text-navy">Cognitive Capital Access</DialogTitle>
          </div>
          <DialogDescription className="text-slate-500">
            Sign in to your OCC account or enrol to unlock the Syllabus Architect, Socratic Tutor, and Essay Lab.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="grid grid-cols-2 w-full bg-navy-soft">
            <TabsTrigger value="login" data-testid="auth-tab-login">Sign In</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Enrol</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            {!showForgot ? (
              <form onSubmit={submitLogin} className="space-y-4 pt-4">
                <div className="space-y-1.5">
                  <Label htmlFor="l-email">Email</Label>
                  <Input id="l-email" type="email" name="email" autoComplete="username" required value={form.email} onChange={update("email")}
                    data-testid="login-email-input" placeholder="scholar@occ.edu" />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="l-pass">Password</Label>
                    <button type="button" onClick={() => { setShowForgot(true); setForgotSent(false); }}
                      data-testid="forgot-password-link"
                      className="text-xs font-medium text-persian-rose hover:underline">
                      Forgot password?
                    </button>
                  </div>
                  <Input id="l-pass" type="password" name="password" autoComplete="current-password" required value={form.password} onChange={update("password")}
                    data-testid="login-password-input" placeholder="••••••••" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-button"
                  className="w-full bg-navy hover:bg-navy-light text-white">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Sign In
                </Button>
              </form>
            ) : forgotSent ? (
              <div className="space-y-4 pt-4 text-center" data-testid="forgot-sent-message">
                <div className="mx-auto h-12 w-12 rounded-full bg-persian-blue/15 flex items-center justify-center">
                  <MailCheck className="h-6 w-6 text-persian-blue" />
                </div>
                <p className="text-sm text-slate-600">
                  If an account exists for that email, a password reset link is on its way. It expires in 1 hour.
                </p>
                <Button type="button" variant="ghost" onClick={() => setShowForgot(false)}
                  data-testid="forgot-back-button" className="text-navy">
                  <ArrowLeft className="h-4 w-4 mr-2" />Back to sign in
                </Button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-4 pt-4" data-testid="forgot-form">
                <p className="text-sm text-slate-500">
                  Enter your account email and we'll send you a secure link to reset your password.
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="f-email">Email</Label>
                  <Input id="f-email" type="email" name="email" autoComplete="username" required
                    data-testid="forgot-email-input" placeholder="scholar@occ.edu" />
                </div>
                <Button type="submit" disabled={loading} data-testid="forgot-submit-button"
                  className="w-full bg-persian-rose hover:bg-persian-rosehover text-white">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Send reset link
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowForgot(false)}
                  data-testid="forgot-cancel-button" className="w-full text-navy">
                  <ArrowLeft className="h-4 w-4 mr-2" />Back to sign in
                </Button>
              </form>
            )}
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={submitRegister} className="space-y-4 pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="r-name">Full Name</Label>
                <Input id="r-name" name="name" autoComplete="name" required value={form.name} onChange={update("name")}
                  data-testid="register-name-input" placeholder="Ada Lovelace" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="r-email">Email</Label>
                <Input id="r-email" type="email" name="email" autoComplete="username" required value={form.email} onChange={update("email")}
                  data-testid="register-email-input" placeholder="scholar@occ.edu" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="r-pass">Password</Label>
                <Input id="r-pass" type="password" name="password" autoComplete="new-password" required minLength={6} value={form.password} onChange={update("password")}
                  data-testid="register-password-input" placeholder="At least 6 characters" />
              </div>
              <Button type="submit" disabled={loading} data-testid="register-submit-button"
                className="w-full bg-persian-rose hover:bg-persian-rosehover text-white">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enrol Now
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
